<template>
  <div ref="pageContainer" class="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <img
        v-for="(bee, index) in bees"
        :key="index"
        :src="beeImage"
        alt="Bee"
        class="absolute w-12 h-12 transition-none"
        :style="{
          left: bee.x + 'px',
          top: bee.y + 'px',
          transform: `scaleX(${bee.direction})`,
        }"
      />
    </div>
    
    <div ref="contentContainer" class="max-w-md w-full space-y-8 relative z-10">
      <div class="bg-white rounded-lg shadow-lg p-8 border border-creme-300">
        <div>
          <h2 class="mt-6 text-center text-3xl font-marketing font-bold text-primary">
            Reset Password
          </h2>
          <p class="mt-2 text-center text-sm text-gray-900 font-sans">
            Enter your new password
          </p>
        </div>
        
        <form class="mt-8 space-y-6" @submit.prevent="handleResetPassword">
          <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm font-sans">
            {{ error }}
          </div>
          <div v-if="success" class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm font-sans">
            {{ success }}
          </div>
          <div class="space-y-4">
            <div>
              <label for="password" class="block text-sm font-marketing font-medium text-primary mb-1">
                New Password
              </label>
              <input
                id="password"
                v-model="password"
                type="password"
                required
                minlength="8"
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 sm:text-sm font-sans"
                placeholder="Enter your new password"
              />
            </div>
            
            <div>
              <label for="confirmPassword" class="block text-sm font-marketing font-medium text-primary mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                type="password"
                required
                minlength="8"
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 sm:text-sm font-sans"
                placeholder="Confirm your new password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="loading || !password || !confirmPassword"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-marketing font-medium rounded-md text-white bg-accent hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? 'Resetting...' : 'Reset Password' }}
            </button>
          </div>

          <div class="text-center">
            <p class="text-sm text-gray-900 font-sans">
              Remember your password?
              <a href="/login" class="font-marketing font-medium text-accent hover:text-green-700 transition-colors">
                Login
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { authService } from '../services/auth'

const pageContainer = ref(null)
const contentContainer = ref(null)
const password = ref('')
const confirmPassword = ref('')
const error = ref('')
const success = ref('')
const loading = ref(false)

const token = computed(() => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('token')
})

const beeImage = '/logos/Icon Logo/No Background Black Logo Icon Cropped.png'

const bees = ref([
  { x: 100, y: 150, vx: 0.8, vy: 0.6, direction: 1 },
  { x: 300, y: 200, vx: -0.7, vy: 0.5, direction: -1 },
  { x: 500, y: 100, vx: 0.9, vy: -0.8, direction: 1 },
  { x: 700, y: 300, vx: -0.6, vy: -0.7, direction: -1 },
  { x: 900, y: 250, vx: 0.75, vy: 0.65, direction: 1 },
])

let animationFrame = null

const updateBees = () => {
  if (!pageContainer.value) {
    animationFrame = requestAnimationFrame(updateBees)
    return
  }

  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerLeft = containerRect.left
  const containerRight = containerRect.right
  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom
  const containerWidth = containerRight - containerLeft
  const containerHeight = containerBottom - containerTop

  const cardCenterX = containerLeft + containerWidth / 2
  const cardCenterY = containerTop + containerHeight / 2
  const cardLeft = cardCenterX - 224
  const cardRight = cardCenterX + 224
  const cardTop = cardCenterY - 300
  const cardBottom = cardCenterY + 300

  bees.value.forEach(bee => {
    const prevVxSign = Math.sign(bee.vx)
    const prevX = bee.x
    const prevY = bee.y
    
    bee.x += bee.vx
    bee.y += bee.vy

    if (bee.x < 0) {
      bee.x = 0
      if (bee.vx < 0) {
        bee.vx = -bee.vx
        bee.x = Math.max(0, bee.x + bee.vx * 2)
      }
    } else if (bee.x > containerWidth - 48) {
      bee.x = containerWidth - 48
      if (bee.vx > 0) {
        bee.vx = -bee.vx
        bee.x = Math.min(containerWidth - 48, bee.x + bee.vx * 2)
      }
    }

    if (bee.y < 0) {
      bee.y = 0
      if (bee.vy < 0) {
        bee.vy = -bee.vy
        bee.y = Math.max(0, bee.y + bee.vy * 2)
      }
    } else if (bee.y > containerHeight - 48) {
      bee.y = containerHeight - 48
      if (bee.vy > 0) {
        bee.vy = -bee.vy
        bee.y = Math.min(containerHeight - 48, bee.y + bee.vy * 2)
      }
    }

    const beeCenterX = containerLeft + bee.x + 24
    const beeCenterY = containerTop + bee.y + 24
    const prevBeeCenterX = containerLeft + prevX + 24
    const prevBeeCenterY = containerTop + prevY + 24
    const wasInsideCard = prevBeeCenterX > cardLeft && prevBeeCenterX < cardRight && prevBeeCenterY > cardTop && prevBeeCenterY < cardBottom

    if (
      beeCenterX > cardLeft &&
      beeCenterX < cardRight &&
      beeCenterY > cardTop &&
      beeCenterY < cardBottom
    ) {
      if (!wasInsideCard) {
        const distanceFromLeft = beeCenterX - cardLeft
        const distanceFromRight = cardRight - beeCenterX
        const distanceFromTop = beeCenterY - cardTop
        const distanceFromBottom = cardBottom - beeCenterY

        const minDistance = Math.min(distanceFromLeft, distanceFromRight, distanceFromTop, distanceFromBottom)

        if (minDistance === distanceFromLeft || minDistance === distanceFromRight) {
          if ((minDistance === distanceFromLeft && bee.vx < 0) || (minDistance === distanceFromRight && bee.vx > 0)) {
            bee.vx = -bee.vx
            bee.x = prevX + bee.vx * 2
          }
        } else {
          if ((minDistance === distanceFromTop && bee.vy < 0) || (minDistance === distanceFromBottom && bee.vy > 0)) {
            bee.vy = -bee.vy
            bee.y = prevY + bee.vy * 2
          }
        }
      }
    }

    const newVxSign = Math.sign(bee.vx)
    if (prevVxSign !== 0 && newVxSign !== 0 && prevVxSign !== newVxSign) {
      bee.direction = newVxSign
    }
  })

  animationFrame = requestAnimationFrame(updateBees)
}

const handleResize = () => {
  if (!pageContainer.value) return
  
  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  bees.value.forEach(bee => {
    bee.x = Math.min(bee.x, containerWidth - 48)
    bee.y = Math.min(bee.y, containerHeight - 48)
  })
}

onMounted(() => {
  if (!token.value) {
    error.value = 'Invalid reset link. Please request a new password reset.'
    return
  }

  if (!pageContainer.value) return
  
  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  bees.value = [
    { x: containerWidth * 0.1, y: containerHeight * 0.2, vx: 0.8, vy: 0.6, direction: 1 },
    { x: containerWidth * 0.3, y: containerHeight * 0.3, vx: -0.7, vy: 0.5, direction: -1 },
    { x: containerWidth * 0.5, y: containerHeight * 0.15, vx: 0.9, vy: -0.8, direction: 1 },
    { x: containerWidth * 0.7, y: containerHeight * 0.4, vx: -0.6, vy: -0.7, direction: -1 },
    { x: containerWidth * 0.9, y: containerHeight * 0.3, vx: 0.75, vy: 0.65, direction: 1 },
  ]
  
  updateBees()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
  window.removeEventListener('resize', handleResize)
})

const handleResetPassword = async () => {
  error.value = ''
  success.value = ''
  
  if (!token.value) {
    error.value = 'Invalid reset link. Please request a new password reset.'
    return
  }

  if (password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match.'
    return
  }

  if (password.value.length < 8) {
    error.value = 'Password must be at least 8 characters long.'
    return
  }

  loading.value = true
  
  try {
    const result = await authService.resetPassword(token.value, password.value)
    
    if (result.success) {
      success.value = result.data?.message || 'Password has been reset successfully. Redirecting to login...'
      setTimeout(() => {
        window.location.href = '/login'
      }, 2000)
    } else {
      error.value = typeof result.error === 'string' ? result.error : 'Failed to reset password. Please try again.'
    }
  } catch (err) {
    error.value = 'An unexpected error occurred. Please try again.'
  } finally {
    loading.value = false
  }
}

</script>
